import { useEffect } from 'react';

export default function usePdfHighlightApplication({
  containerRef,
  numPages,
  highlights,
  currentPage,
  removeHighlight,
  handleRemoveHighlightById,
}) {
  useEffect(() => {
    if (!numPages) {
      const allHighlightedSpans = document.querySelectorAll('.react-pdf__Page__textContent span[data-highlight-id]');
      allHighlightedSpans.forEach((span) => {
        const parent = span.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(span.textContent), span);
          parent.normalize();
        }
      });

      document.querySelectorAll('.react-pdf__Page .pdf-highlight-overlay').forEach((overlay) => overlay.remove());
      return;
    }

    if (!highlights || highlights.length === 0) {
      const allHighlightedSpans = document.querySelectorAll('.react-pdf__Page__textContent span[data-highlight-id]');
      allHighlightedSpans.forEach((span) => {
        const parent = span.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(span.textContent), span);
          parent.normalize();
        }
      });

      document.querySelectorAll('.react-pdf__Page .pdf-highlight-overlay').forEach((overlay) => overlay.remove());
      return;
    }

    let retryCount = 0;
    const maxRetries = 30;

    const applyHighlights = () => {
      const textLayers = document.querySelectorAll('.react-pdf__Page__textContent');

      const hasTextContent = Array.from(textLayers).some((layer) => {
        const textNodes = layer.querySelectorAll('span');
        return textNodes.length > 0 && Array.from(textNodes).some((span) => span.textContent.trim().length > 0);
      });

      if (textLayers.length === 0 || !hasTextContent) {
        retryCount++;
        if (retryCount < maxRetries) {
          const delay = retryCount <= 10 ? 50 : retryCount <= 20 ? 100 : 200;
          setTimeout(applyHighlights, delay);
          return;
        }
        return;
      }

      retryCount = 0;

      const currentHighlightIds = new Set(highlights.map((h) => h.id.toString()));

      const allHighlightedSpans = document.querySelectorAll('.react-pdf__Page__textContent span[data-highlight-id]');
      allHighlightedSpans.forEach((span) => {
        const highlightId = span.getAttribute('data-highlight-id');
        if (!currentHighlightIds.has(highlightId)) {
          const parent = span.parentNode;
          if (parent) {
            const textNode = document.createTextNode(span.textContent);
            try {
              parent.replaceChild(textNode, span);
              parent.normalize();
              console.log('✅ Removed highlight span from DOM:', highlightId);
            } catch (e) {
              console.warn('Failed to remove highlight span:', e, highlightId);
              try {
                const textContent = span.textContent;
                span.outerHTML = textContent;
              } catch (e2) {
                console.error('Failed to remove highlight span (fallback):', e2);
              }
            }
          }
        }
      });

      const allOverlayRects = document.querySelectorAll('.react-pdf__Page .pdf-highlight-overlay [data-highlight-id]');
      allOverlayRects.forEach((el) => {
        const hid = el.getAttribute('data-highlight-id');
        if (hid && !currentHighlightIds.has(hid)) {
          el.remove();
        }
      });

      textLayers.forEach((textLayer) => {
        const pageElement = textLayer.closest('[data-page-number]');
        const pageNumber = pageElement ? parseInt(pageElement.getAttribute('data-page-number'), 10) : null;

        highlights.forEach((highlight) => {
          const highlightText = highlight.text.replace(/^\s+|\s+$/g, '');
          if (!highlightText) return;

          const highlightId = highlight.id.toString();
          const isCitationHighlight = !!highlight.disable_remove;

          const existingSpanHighlight = textLayer.querySelector(`span[data-highlight-id="${highlightId}"]`);
          const existingOverlayHighlight = pageElement?.querySelector(`.pdf-highlight-overlay [data-highlight-id="${highlightId}"]`);
          if (existingSpanHighlight || existingOverlayHighlight) {
            return;
          }

          if (highlight.page_number && pageNumber) {
            if (highlight.page_number !== pageNumber) {
              return;
            }
          } else if (highlight.page_number && !pageNumber) {
            return;
          }

          const hasNewlines = highlightText.includes('\n') || highlightText.includes('\r');

          let contextBefore = (highlight.context_before || '').toString();
          let contextAfter = (highlight.context_after || '').toString();

          if ((!contextBefore && !contextAfter) && typeof highlight.section_name === 'string' && highlight.section_name.includes('|')) {
            const parts = highlight.section_name.split('|');
            if (parts.length >= 3) {
              contextBefore = (parts[0] || '').trim();
              contextAfter = (parts[2] || '').trim();
            }
          }

          const getPlainText = (element) => {
            let text = '';
            const walker = document.createTreeWalker(
              element,
              NodeFilter.SHOW_TEXT,
              {
                acceptNode: (node) => {
                  let parent = node.parentElement;
                  while (parent && parent !== element) {
                    if (parent.hasAttribute && parent.getAttribute('data-highlight-id')) {
                      return NodeFilter.FILTER_REJECT;
                    }
                    parent = parent.parentElement;
                  }
                  return NodeFilter.FILTER_ACCEPT;
                },
              }
            );
            let textNode;
            while (textNode = walker.nextNode()) {
              text += textNode.textContent;
            }
            return text;
          };

          const plainText = getPlainText(textLayer);

          const normalizeWhitespace = (text) => text.replace(/\s+/g, ' ').trim();
          const normalizedHighlightText = normalizeWhitespace(highlightText);

          const getTextNodesInPage = (layer) => {
            const nodes = [];
            const walker = document.createTreeWalker(
              layer,
              NodeFilter.SHOW_TEXT,
              {
                acceptNode: (node) => {
                  let parent = node.parentElement;
                  while (parent && parent !== layer) {
                    if (parent.hasAttribute && parent.getAttribute('data-highlight-id')) {
                      return NodeFilter.FILTER_REJECT;
                    }
                    parent = parent.parentElement;
                  }
                  return node.textContent && node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
                },
              }
            );
            let node;
            while ((node = walker.nextNode())) {
              nodes.push(node);
            }
            return nodes;
          };

          const findRangeForHighlight = (textNodes, sourceHighlightText, normalizedSourceHighlightText, sourceContextBefore, sourceContextAfter) => {
            console.log('🔍 findRangeForHighlight called (Strict Stream):', {
              textNodesCount: textNodes.length,
              highlightText: sourceHighlightText.substring(0, 50),
              normalizedHighlightText: normalizedSourceHighlightText.substring(0, 50),
              hasContextBefore: !!sourceContextBefore,
              hasContextAfter: !!sourceContextAfter,
              contextBefore: sourceContextBefore?.substring(0, 30),
              contextAfter: sourceContextAfter?.substring(0, 30),
            });

            const normalizeForStrictMatch = (str) => {
              if (!str) return '';
              return String(str)
                .replace(/\u00ad/g, '')
                .replace(/\u00a0/g, ' ')
                .replace(/\ufb00/g, 'ff')
                .replace(/\ufb01/g, 'fi')
                .replace(/\ufb02/g, 'fl')
                .replace(/\ufb03/g, 'ffi')
                .replace(/\ufb04/g, 'ffl')
                .replace(/[\u2018\u2019\u201b\u2032]/g, "'")
                .replace(/[\u201c\u201d\u2033]/g, '"')
                .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, '-')
                .replace(/\s+/g, ' ')
                .trim();
            };

            const toStrictComparable = (str) => {
              const normalized = normalizeForStrictMatch(str)
                .normalize ? normalizeForStrictMatch(str).normalize('NFKD') : normalizeForStrictMatch(str);
              return normalized
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '');
            };

            const stripSpaces = (str) => toStrictComparable(str);

            const isIgnorableForStrict = (ch) => {
              if (!ch) return true;
              return !/[a-z0-9]/i.test(ch);
            };

            const strictHighlight = stripSpaces(sourceHighlightText);
            const strictContextBefore = stripSpaces(sourceContextBefore);
            const strictContextAfter = stripSpaces(sourceContextAfter);

            if (!strictHighlight) {
              console.warn('❌ Empty highlight text after stripping spaces');
              return { isValid: false };
            }

            let strictBuffer = '';
            const nodeMap = [];

            textNodes.forEach((node, index) => {
              const nodeText = node.textContent || '';
              const strictNodeText = stripSpaces(nodeText);
              const startIndex = strictBuffer.length;
              strictBuffer += strictNodeText;

              nodeMap.push({
                node,
                originalText: nodeText,
                startIndex,
                endIndex: strictBuffer.length,
                length: strictNodeText.length,
              });
            });

            console.log('📊 Strict buffer built:', {
              bufferLength: strictBuffer.length,
              nodesMapped: nodeMap.length,
              bufferPreview: strictBuffer.substring(0, 100),
            });

            let bufferStartIndex = -1;

            if (strictContextBefore && strictContextBefore.length > 0) {
              const contextIndex = strictBuffer.indexOf(strictContextBefore);
              if (contextIndex !== -1) {
                bufferStartIndex = contextIndex + strictContextBefore.length;
                console.log('✅ Found start via contextBefore:', {
                  contextIndex,
                  bufferStartIndex,
                  contextPreview: strictContextBefore.substring(0, 30),
                });
              }
            }

            if (bufferStartIndex === -1) {
              bufferStartIndex = strictBuffer.indexOf(strictHighlight);
              if (bufferStartIndex !== -1) {
                console.log('✅ Found start via direct match:', bufferStartIndex);
              } else {
                const startSnippet = strictHighlight.substring(0, 30);
                if (startSnippet.length >= 10) {
                  bufferStartIndex = strictBuffer.indexOf(startSnippet);
                  if (bufferStartIndex !== -1) {
                    console.log('✅ Found start via fuzzy match (start snippet):', bufferStartIndex);
                  }
                }
              }
            }

            if (bufferStartIndex === -1) {
              console.warn('❌ Start position not found in strict buffer');
              return { isValid: false };
            }

            let bufferEndIndex = -1;

            if (strictContextAfter && strictContextAfter.length > 0) {
              const searchFrom = bufferStartIndex + strictHighlight.length;
              const searchStart = Math.max(bufferStartIndex, searchFrom - 20);
              const contextIndex = strictBuffer.indexOf(strictContextAfter, searchStart);

              if (contextIndex !== -1) {
                bufferEndIndex = contextIndex;
                console.log('✅ Found end via contextAfter:', {
                  contextIndex,
                  bufferEndIndex,
                  searchStart,
                  contextPreview: strictContextAfter.substring(0, 30),
                });
              }
            }

            if (bufferEndIndex === -1) {
              const endSnippet = strictHighlight.substring(strictHighlight.length - 30);
              if (endSnippet.length >= 10) {
                const minEndSearch = bufferStartIndex + Math.floor(strictHighlight.length * 0.8);
                const maxEndSearch = bufferStartIndex + Math.floor(strictHighlight.length * 1.5);

                const foundEndIndex = strictBuffer.indexOf(endSnippet, minEndSearch);

                if (foundEndIndex !== -1 && foundEndIndex <= maxEndSearch) {
                  bufferEndIndex = foundEndIndex + endSnippet.length;
                  console.log('✅ Found end via fuzzy match (end snippet):', bufferEndIndex);
                }
              }

              if (bufferEndIndex === -1) {
                bufferEndIndex = bufferStartIndex + strictHighlight.length;
                console.log('✅ Found end via length calculation (fallback):', bufferEndIndex);
              }
            }

            const findNodeForIndex = (strictIndex) => {
              const match = nodeMap.find((item) => strictIndex >= item.startIndex && strictIndex < item.endIndex);

              if (!match && strictIndex === strictBuffer.length) {
                const last = nodeMap[nodeMap.length - 1];
                return {
                  node: last.node,
                  offset: last.originalText.length,
                };
              }

              if (!match) return null;

              const offsetInStrict = strictIndex - match.startIndex;
              const text = match.originalText;
              let originalOffset = text.length;
              let strictCounter = 0;

              for (let i = 0; i < text.length; i++) {
                if (!isIgnorableForStrict(text[i])) {
                  if (strictCounter === offsetInStrict) {
                    originalOffset = i;
                    break;
                  }
                  strictCounter++;
                }
              }

              if (strictCounter < offsetInStrict) {
                originalOffset = text.length;
              }

              console.log('📍 Mapped strict offset to original:', {
                strictIndex,
                offsetInStrict,
                originalOffset,
                nodeText: text.substring(0, 50),
                nodeTextLength: text.length,
                strictCounter,
                nodeStartIndex: match.startIndex,
                nodeEndIndex: match.endIndex,
                charAtOffset: text[originalOffset] || '(end)',
              });

              return { node: match.node, offset: originalOffset };
            };

            const getOriginalOffset = (nodeItem, strictOffsetInNode) => {
              let strictCount = 0;
              const text = nodeItem.originalText;

              for (let i = 0; i < text.length; i++) {
                if (!isIgnorableForStrict(text[i])) {
                  if (strictCount === strictOffsetInNode) {
                    return i;
                  }
                  strictCount++;
                }
              }

              return text.length;
            };

            const startPoint = findNodeForIndex(bufferStartIndex);

            if (!startPoint) {
              console.warn('❌ Could not map start index to node:', bufferStartIndex);
              return { isValid: false };
            }

            const endNodeMatch = nodeMap.find((item) => bufferEndIndex > item.startIndex && bufferEndIndex <= item.endIndex);

            if (!endNodeMatch) {
              if (bufferEndIndex === strictBuffer.length) {
                const last = nodeMap[nodeMap.length - 1];
                return {
                  startNode: startPoint.node,
                  startOffset: startPoint.offset,
                  endNode: last.node,
                  endOffset: last.originalText.length,
                  isValid: true,
                };
              }
              console.warn('❌ Could not map end index to node:', bufferEndIndex);
              return { isValid: false };
            }

            const endOffset = getOriginalOffset(endNodeMatch, bufferEndIndex - endNodeMatch.startIndex);

            const result = {
              startNode: startPoint.node,
              startOffset: Math.max(0, Math.min(startPoint.offset, startPoint.node.textContent.length)),
              endNode: endNodeMatch.node,
              endOffset: Math.max(0, Math.min(endOffset, endNodeMatch.node.textContent.length)),
              isValid: true,
            };

            console.log('✅ findRangeForHighlight SUCCESS (Strict Stream):', {
              startNode: startPoint.node.textContent.substring(0, 30),
              startOffset: result.startOffset,
              endNode: endNodeMatch.node.textContent.substring(0, 30),
              endOffset: result.endOffset,
              startNodeLength: startPoint.node.textContent.length,
              endNodeLength: endNodeMatch.node.textContent.length,
              bufferStartIndex,
              bufferEndIndex,
            });

            return result;
          };

          let textFound = false;

          const toStrictComparable = (str) => {
            if (!str) return '';
            return str.toLowerCase().replace(/[^a-z0-9]+/g, '');
          };

          const strictHighlight = toStrictComparable(highlightText);
          const strictPlainText = toStrictComparable(plainText);

          if (strictPlainText.includes(strictHighlight)) {
            textFound = true;
            console.log('✅ Strict match found:', highlightId);
          } else {
            const first30 = strictHighlight.substring(0, 30);
            if (first30.length >= 10 && strictPlainText.includes(first30)) {
              textFound = true;
              console.log('✅ Fuzzy match found (start):', highlightId);
            } else {
              const last30 = strictHighlight.substring(strictHighlight.length - 30);
              if (last30.length >= 10 && strictPlainText.includes(last30)) {
                textFound = true;
                console.log('✅ Fuzzy match found (end):', highlightId);
              }
            }
          }

          if (!textFound) {
            console.warn('❌ Highlight text not found in PDF:', {
              highlightId,
              highlightText: highlightText.substring(0, 100),
              highlightTextLength: highlightText.length,
              strictHighlight: strictHighlight.substring(0, 50),
              strictPlainText: strictPlainText.substring(0, 200),
            });
            return;
          }

          const potentialMatches = [];
          const allTextNodes = getTextNodesInPage(textLayer);

          if (allTextNodes.length > 1) {
            const rangeResult = findRangeForHighlight(
              allTextNodes,
              highlightText,
              normalizedHighlightText,
              contextBefore,
              contextAfter
            );

            if (rangeResult.isValid && rangeResult.startNode && rangeResult.endNode) {
              console.log('✅ Stream-based match found:', {
                highlightId,
                startNode: rangeResult.startNode.textContent.substring(0, 30),
                endNode: rangeResult.endNode.textContent.substring(0, 30),
                startOffset: rangeResult.startOffset,
                endOffset: rangeResult.endOffset,
                startNodeLength: rangeResult.startNode.textContent.length,
                endNodeLength: rangeResult.endNode.textContent.length,
                hasNewlines,
              });

              potentialMatches.push({
                node: rangeResult.startNode,
                nodeText: rangeResult.startNode.textContent,
                textIndex: rangeResult.startOffset,
                highlightStartInFullText: 0,
                matchScore: 200,
                isMultiline: true,
                startNode: rangeResult.startNode,
                startOffset: rangeResult.startOffset,
                endNode: rangeResult.endNode,
                endOffset: rangeResult.endOffset,
              });
            }
          }

          let node = null;
          const singleNodeWalker = document.createTreeWalker(
            textLayer,
            NodeFilter.SHOW_TEXT,
            {
              acceptNode: (n) => {
                let parent = n.parentElement;
                while (parent && parent !== textLayer) {
                  if (parent.hasAttribute && parent.getAttribute('data-highlight-id')) {
                    return NodeFilter.FILTER_REJECT;
                  }
                  parent = parent.parentElement;
                }
                return NodeFilter.FILTER_ACCEPT;
              },
            }
          );

          while ((node = singleNodeWalker.nextNode())) {
            const nodeText = node.textContent;
            if (!nodeText) continue;

            const normalizedNodeText = normalizeWhitespace(nodeText);
            if (!normalizedNodeText.includes(normalizedHighlightText)) continue;

            let textIndex = nodeText.indexOf(highlightText);

            if (textIndex === -1) {
              const normalizedIndex = normalizedNodeText.indexOf(normalizedHighlightText);
              if (normalizedIndex !== -1) {
                let charCount = 0;
                for (let i = 0; i < nodeText.length && charCount < normalizedIndex; i++) {
                  if (!/\s/.test(nodeText[i])) {
                    charCount++;
                  }
                  if (charCount >= normalizedIndex) {
                    textIndex = i;
                    break;
                  }
                }
              }
            }

            if (textIndex === -1) continue;

            let nodeStartInFullText = 0;
            const positionWalker = document.createTreeWalker(
              textLayer,
              NodeFilter.SHOW_TEXT,
              {
                acceptNode: (n) => {
                  let parent = n.parentElement;
                  while (parent && parent !== textLayer) {
                    if (parent.hasAttribute && parent.getAttribute('data-highlight-id')) {
                      return NodeFilter.FILTER_REJECT;
                    }
                    parent = parent.parentElement;
                  }
                  return NodeFilter.FILTER_ACCEPT;
                },
              }
            );
            let checkNode;
            while (checkNode = positionWalker.nextNode()) {
              if (checkNode === node) break;
              nodeStartInFullText += checkNode.textContent.length;
            }

            const highlightStartInFullText = nodeStartInFullText + textIndex;

            let matchScore = 0;

            if (contextBefore || contextAfter) {
              const hasMeaningfulContext =
                (contextBefore && contextBefore.length >= 10) ||
                (contextAfter && contextAfter.length >= 10);

              if (hasMeaningfulContext) {
                const fullText = plainText;

                if (contextBefore && contextBefore.length >= 10) {
                  const beforeText = fullText.slice(
                    Math.max(0, highlightStartInFullText - 60),
                    highlightStartInFullText
                  ).trim();

                  const normalizedBeforeText = normalizeWhitespace(beforeText).toLowerCase();
                  const normalizedContextBefore = normalizeWhitespace(contextBefore).toLowerCase();

                  if (normalizedBeforeText.endsWith(normalizedContextBefore)) {
                    matchScore += 200;
                  } else {
                    const last30 = normalizedContextBefore.slice(-30);
                    if (last30.length >= 10 && normalizedBeforeText.endsWith(last30)) {
                      matchScore += 100;
                    } else {
                      const last20 = normalizedContextBefore.slice(-20);
                      if (last20.length >= 10 && normalizedBeforeText.endsWith(last20)) {
                        matchScore += 50;
                      } else if (last20.length >= 5 && normalizedBeforeText.includes(last20)) {
                        matchScore += 10;
                      }
                    }
                  }
                }

                if (contextAfter && contextAfter.length >= 10) {
                  const afterText = fullText.slice(
                    highlightStartInFullText + highlightText.length,
                    highlightStartInFullText + highlightText.length + 60
                  ).trim();

                  const normalizedAfterText = normalizeWhitespace(afterText).toLowerCase();
                  const normalizedContextAfter = normalizeWhitespace(contextAfter).toLowerCase();

                  if (normalizedAfterText.startsWith(normalizedContextAfter)) {
                    matchScore += 200;
                  } else {
                    const first30 = normalizedContextAfter.slice(0, 30);
                    if (first30.length >= 10 && normalizedAfterText.startsWith(first30)) {
                      matchScore += 100;
                    } else {
                      const first20 = normalizedContextAfter.slice(0, 20);
                      if (first20.length >= 10 && normalizedAfterText.startsWith(first20)) {
                        matchScore += 50;
                      } else if (first20.length >= 5 && normalizedAfterText.includes(first20)) {
                        matchScore += 10;
                      }
                    }
                  }
                }
              }
            } else {
              matchScore = 1;
            }

            potentialMatches.push({
              node,
              nodeText,
              textIndex,
              highlightStartInFullText,
              matchScore,
            });
          }

          potentialMatches.sort((a, b) => b.matchScore - a.matchScore);

          let highlighted = false;
          for (const match of potentialMatches) {
            if (highlighted) break;

            const { node: matchNode, nodeText, textIndex, isMultiline, startNode, startOffset, endNode, endOffset } = match;

            const range = document.createRange();

            let rangeCreated = false;
            if (isMultiline && startNode && endNode && startOffset !== undefined && endOffset !== undefined) {
              try {
                if (!startNode.parentNode || !endNode.parentNode ||
                  !textLayer.contains(startNode) || !textLayer.contains(endNode)) {
                  continue;
                }

                range.setStart(startNode, Math.max(0, Math.min(startOffset, startNode.textContent.length)));
                range.setEnd(endNode, Math.max(0, Math.min(endOffset, endNode.textContent.length)));

                if (range.collapsed) {
                  continue;
                }

                rangeCreated = true;
                console.log('✅ Multiline range created successfully:', {
                  highlightId,
                  startOffset,
                  endOffset,
                  startNodeLength: startNode.textContent.length,
                  endNodeLength: endNode.textContent.length,
                  rangeCollapsed: range.collapsed,
                });
              } catch (e) {
                console.warn('❌ Failed to create multiline range:', e, {
                  highlightId,
                  startNode: !!startNode,
                  endNode: !!endNode,
                  startOffset,
                  endOffset,
                  error: e.message,
                });
                continue;
              }
            }

            if (!rangeCreated) {
              let startIndex = textIndex;
              let endIndex = textIndex + highlightText.length;

              if (startIndex === -1 || startIndex >= nodeText.length || hasNewlines) {
                const normalizedNodeText = normalizeWhitespace(nodeText);
                const normalizedIndex = normalizedNodeText.indexOf(normalizedHighlightText);

                if (normalizedIndex !== -1) {
                  let originalPos = 0;
                  let normalizedPos = 0;
                  let inWhitespace = false;

                  for (let i = 0; i < nodeText.length; i++) {
                    const isWhitespace = /\s/.test(nodeText[i]);

                    if (isWhitespace && !inWhitespace) {
                      normalizedPos++;
                      inWhitespace = true;
                    } else if (!isWhitespace) {
                      inWhitespace = false;
                      normalizedPos++;
                    }

                    if (normalizedPos > normalizedIndex) {
                      originalPos = i;
                      break;
                    } else if (normalizedPos === normalizedIndex) {
                      originalPos = i;
                      break;
                    }
                  }

                  startIndex = originalPos;

                  const normalizedEnd = normalizedIndex + normalizedHighlightText.length;
                  let endOriginalPos = startIndex;
                  let endNormalizedPos = normalizedIndex;
                  let endInWhitespace = false;

                  for (let i = startIndex; i < nodeText.length && endNormalizedPos < normalizedEnd; i++) {
                    const isWhitespace = /\s/.test(nodeText[i]);

                    if (isWhitespace && !endInWhitespace) {
                      endNormalizedPos++;
                      endInWhitespace = true;
                    } else if (!isWhitespace) {
                      endInWhitespace = false;
                      endNormalizedPos++;
                    }

                    endOriginalPos = i + 1;

                    if (endNormalizedPos >= normalizedEnd) {
                      break;
                    }
                  }

                  endIndex = endOriginalPos;
                } else {
                  const exactIndex = nodeText.indexOf(highlightText);
                  if (exactIndex !== -1) {
                    startIndex = exactIndex;
                    endIndex = exactIndex + highlightText.length;
                  } else {
                    continue;
                  }
                }
              }

              if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
                try {
                  if (!matchNode.parentNode || !textLayer.contains(matchNode)) {
                    continue;
                  }

                  startIndex = Math.max(0, Math.min(startIndex, nodeText.length));
                  endIndex = Math.max(startIndex, Math.min(endIndex, nodeText.length));

                  range.setStart(matchNode, startIndex);
                  range.setEnd(matchNode, endIndex);

                  if (range.collapsed) {
                    continue;
                  }

                  rangeCreated = true;
                } catch (e) {
                  console.warn('Failed to create single-node range:', e);
                  continue;
                }
              } else {
                continue;
              }
            }

            const ensureOverlay = () => {
              if (!pageElement) return null;
              const pageRoot = pageElement.closest('.react-pdf__Page') || pageElement;
              let overlay = pageRoot.querySelector('.pdf-highlight-overlay');
              if (!overlay) {
                overlay = document.createElement('div');
                overlay.className = 'pdf-highlight-overlay';
                pageRoot.appendChild(overlay);
              }
              return overlay;
            };

            const applyOverlayRectsForRange = (sourceRange) => {
              const overlay = ensureOverlay();
              if (!overlay || !pageElement) return false;
              const pageRoot = (pageElement.closest('.react-pdf__Page') || pageElement);
              const baseRect = pageRoot.getBoundingClientRect();
              const rects = Array.from(sourceRange.getClientRects ? sourceRange.getClientRects() : []);
              if (!rects.length) return false;

              rects.forEach((r, idx) => {
                if (!r || r.width < 2 || r.height < 2) return;

                const div = document.createElement('div');
                div.className = 'pdf-highlight-rect';
                div.setAttribute('data-highlight-id', highlightId);
                div.setAttribute('data-rect-index', String(idx));

                const top = r.top - baseRect.top;
                const left = r.left - baseRect.left;
                const height = r.height;
                const width = r.width;
                const shrinkY = height > 6 ? 2 : 0;

                div.style.top = `${Math.max(0, top + shrinkY / 2)}px`;
                div.style.left = `${Math.max(0, left)}px`;
                div.style.width = `${Math.max(0, width)}px`;
                div.style.height = `${Math.max(0, height - shrinkY)}px`;

                overlay.appendChild(div);
              });

              return true;
            };

            if (rangeCreated && !range.collapsed) {
              console.log('🎨 Applying highlight:', {
                highlightId,
                isMultiline: !!isMultiline,
                rangeCollapsed: range.collapsed,
                rangeStart: range.startContainer?.textContent?.substring(0, 20),
                rangeEnd: range.endContainer?.textContent?.substring(0, 20),
              });
              try {
                if (isCitationHighlight) {
                  const ok = applyOverlayRectsForRange(range);
                  if (ok) {
                    highlighted = true;
                    break;
                  }
                }

                const highlightSpan = document.createElement('span');
                highlightSpan.setAttribute('data-highlight-id', highlightId);
                highlightSpan.style.cssText = `
                  background-color: rgba(34, 197, 94, 0.4) !important;
                  cursor: pointer !important;
                  position: relative !important;
                  z-index: 1000 !important;
                  display: inline !important;
                  vertical-align: baseline !important;
                  line-height: inherit !important;
                  opacity: 1 !important;
                  visibility: visible !important;
                  isolation: isolate !important;
                `;

                try {
                  range.surroundContents(highlightSpan);

                  if (!highlight.disable_remove) {
                    const removeBtn = document.createElement('button');
                    removeBtn.className = 'highlight-remove-btn';
                    removeBtn.innerHTML = '×';
                    removeBtn.setAttribute('aria-label', 'Remove highlight');
                    removeBtn.setAttribute('data-highlight-id', highlightId);
                    removeBtn.onclick = (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const btnHighlightId = removeBtn.getAttribute('data-highlight-id');
                      if (btnHighlightId && removeHighlight) {
                        const numericId = parseInt(btnHighlightId, 10);
                        if (!isNaN(numericId)) {
                          handleRemoveHighlightById(numericId);
                        }
                      }
                    };
                    highlightSpan.appendChild(removeBtn);
                  }

                  highlighted = true;

                  const insertedSpan = textLayer.querySelector(`span[data-highlight-id="${highlightId}"]`);
                  if (insertedSpan) {
                    const computedStyle = window.getComputedStyle(insertedSpan);
                    console.log('✅ Highlight applied successfully (surroundContents):', {
                      highlightId,
                      inDOM: true,
                      backgroundColor: computedStyle.backgroundColor,
                      display: computedStyle.display,
                      visibility: computedStyle.visibility,
                      opacity: computedStyle.opacity,
                      textContent: insertedSpan.textContent.substring(0, 50),
                    });
                  } else {
                    console.error('❌ Highlight span not found in DOM after surroundContents:', highlightId);
                  }
                  break;
                } catch (surroundError) {
                  console.log('⚠️ surroundContents failed, trying NEW multiline approach:', {
                    highlightId,
                    error: surroundError.message,
                  });

                  let newApproachError = null;
                  if (isMultiline && startNode && endNode) {
                    try {
                      if (range.collapsed || !range.startContainer || !range.endContainer) {
                        continue;
                      }

                      const actualStartContainer = range.startContainer;
                      const actualStartOffset = range.startOffset;
                      const actualEndContainer = range.endContainer;
                      const actualEndOffset = range.endOffset;

                      console.log('📍 Using range offsets:', {
                        highlightId,
                        actualStartContainer: actualStartContainer.textContent?.substring(0, 30),
                        actualStartOffset,
                        actualEndContainer: actualEndContainer.textContent?.substring(0, 30),
                        actualEndOffset,
                        calculatedStartOffset: startOffset,
                        calculatedEndOffset: endOffset,
                      });

                      const textNodes = [];

                      if (actualStartContainer.nodeType === Node.TEXT_NODE) {
                        textNodes.push(actualStartContainer);
                      }
                      if (actualEndContainer !== actualStartContainer && actualEndContainer.nodeType === Node.TEXT_NODE) {
                        textNodes.push(actualEndContainer);
                      }

                      const commonAncestor = range.commonAncestorContainer;
                      const walker = document.createTreeWalker(
                        commonAncestor,
                        NodeFilter.SHOW_TEXT,
                        null
                      );

                      let foundStart = false;
                      let middleNode;
                      while ((middleNode = walker.nextNode())) {
                        if (middleNode.nodeType !== Node.TEXT_NODE || !middleNode.textContent.trim()) {
                          continue;
                        }

                        if (middleNode === actualStartContainer) {
                          foundStart = true;
                          continue;
                        }

                        if (foundStart) {
                          if (middleNode === actualEndContainer) {
                            break;
                          }

                          if (!textNodes.includes(middleNode)) {
                            textNodes.push(middleNode);
                          }
                        }
                      }

                      if (actualEndContainer !== actualStartContainer && actualEndContainer.nodeType === Node.TEXT_NODE) {
                        const endIndex = textNodes.indexOf(actualEndContainer);
                        if (endIndex !== -1 && endIndex !== textNodes.length - 1) {
                          textNodes.splice(endIndex, 1);
                        }
                        if (!textNodes.includes(actualEndContainer)) {
                          textNodes.push(actualEndContainer);
                        }
                      }

                      console.log('🔍 Collected text nodes for multiline highlight:', {
                        highlightId,
                        startNode: actualStartContainer.textContent?.substring(0, 30),
                        endNode: actualEndContainer.textContent?.substring(0, 30),
                        totalNodes: textNodes.length,
                        nodeTexts: textNodes.map((n) => n.textContent?.substring(0, 20)),
                      });

                      if (textNodes.length === 0) {
                        console.warn('⚠️ No text nodes found for multiline highlight:', highlightId);
                        continue;
                      }

                      console.log('🔧 NEW APPROACH: Wrapping', textNodes.length, 'text nodes individually');

                      const highlightSpans = [];
                      textNodes.forEach((textNode, index) => {
                        if (textNode.parentElement?.hasAttribute?.('data-highlight-id')) {
                          return;
                        }

                        let nodeStartOffset = 0;
                        let nodeEndOffset = textNode.textContent.length;

                        const isStartNode = textNode === actualStartContainer;
                        const isEndNode = textNode === actualEndContainer;

                        if (isStartNode) {
                          nodeStartOffset = Math.max(0, Math.min(actualStartOffset, textNode.textContent.length));
                          console.log('📍 Start node offset:', {
                            highlightId,
                            nodeText: textNode.textContent.substring(0, 30),
                            actualStartOffset,
                            nodeStartOffset,
                            nodeLength: textNode.textContent.length,
                          });
                        }

                        if (isEndNode) {
                          nodeEndOffset = Math.max(nodeStartOffset, Math.min(actualEndOffset, textNode.textContent.length));
                          console.log('📍 End node offset:', {
                            highlightId,
                            nodeText: textNode.textContent.substring(0, 30),
                            actualEndOffset,
                            nodeEndOffset,
                            nodeLength: textNode.textContent.length,
                          });
                        }

                        if (nodeStartOffset === 0 && nodeEndOffset === textNode.textContent.length) {
                          const nodeSpan = document.createElement('span');
                          nodeSpan.setAttribute('data-highlight-id', highlightId);
                          nodeSpan.setAttribute('data-highlight-part', `${index + 1}/${textNodes.length}`);
                          nodeSpan.className = 'multiline-highlight-part';
                          nodeSpan.style.cssText = `
                            background-color: rgba(34, 197, 94, 0.4) !important;
                            cursor: pointer !important;
                            position: relative !important;
                            z-index: 10 !important;
                            display: inline !important;
                            vertical-align: baseline !important;
                            line-height: inherit !important;
                            opacity: 1 !important;
                            visibility: visible !important;
                          `;

                          textNode.parentNode?.replaceChild(nodeSpan, textNode);
                          nodeSpan.appendChild(textNode);

                          highlightSpans.push(nodeSpan);
                        } else {
                          const beforeText = textNode.textContent.substring(0, nodeStartOffset);
                          const selectedText = textNode.textContent.substring(nodeStartOffset, nodeEndOffset);
                          const afterText = textNode.textContent.substring(nodeEndOffset);

                          const parent = textNode.parentNode;
                          if (!parent) return;

                          if (beforeText) {
                            const beforeNode = document.createTextNode(beforeText);
                            parent.insertBefore(beforeNode, textNode);
                          }

                          if (afterText) {
                            const afterNode = document.createTextNode(afterText);
                            parent.insertBefore(afterNode, textNode.nextSibling);
                          }

                          const nodeSpan = document.createElement('span');
                          nodeSpan.setAttribute('data-highlight-id', highlightId);
                          nodeSpan.setAttribute('data-highlight-part', `${index + 1}/${textNodes.length}`);
                          nodeSpan.className = 'multiline-highlight-part';
                          nodeSpan.style.cssText = `
                            background-color: rgba(34, 197, 94, 0.4) !important;
                            cursor: pointer !important;
                            position: relative !important;
                            z-index: 10 !important;
                            display: inline !important;
                            vertical-align: baseline !important;
                            line-height: inherit !important;
                            opacity: 1 !important;
                            visibility: visible !important;
                          `;

                          textNode.textContent = selectedText;

                          parent.replaceChild(nodeSpan, textNode);
                          nodeSpan.appendChild(textNode);

                          highlightSpans.push(nodeSpan);
                        }
                      });

                      if (highlightSpans.length > 0) {
                        const firstSpan = highlightSpans[0];

                        if (!highlight.disable_remove) {
                          const removeBtn = document.createElement('button');
                          removeBtn.className = 'highlight-remove-btn';
                          removeBtn.innerHTML = '×';
                          removeBtn.setAttribute('aria-label', 'Remove highlight');
                          removeBtn.setAttribute('data-highlight-id', highlightId);
                          removeBtn.onclick = (e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const btnHighlightId = removeBtn.getAttribute('data-highlight-id');
                            if (btnHighlightId && removeHighlight) {
                              const numericId = parseInt(btnHighlightId, 10);
                              if (!isNaN(numericId)) {
                                handleRemoveHighlightById(numericId);
                              }
                            }
                          };
                          firstSpan.appendChild(removeBtn);
                        }

                        const insertedSpans = textLayer.querySelectorAll(`span[data-highlight-id="${highlightId}"]`);
                        if (insertedSpans.length > 0) {
                          const firstInsertedSpan = insertedSpans[0];
                          const computedStyle = window.getComputedStyle(firstInsertedSpan);
                          console.log('✅ NEW APPROACH: Created', insertedSpans.length, 'highlight spans:', {
                            highlightId,
                            spansCreated: highlightSpans.length,
                            spansInDOM: insertedSpans.length,
                            firstSpanText: firstInsertedSpan.textContent.substring(0, 30),
                            backgroundColor: computedStyle.backgroundColor,
                            display: computedStyle.display,
                            visibility: computedStyle.visibility,
                            opacity: computedStyle.opacity,
                          });
                        } else {
                          console.error('❌ No highlight spans found in DOM after creation:', {
                            highlightId,
                            spansCreated: highlightSpans.length,
                          });
                        }

                        highlighted = true;
                        console.log('✅ Highlight applied successfully (NEW multiline approach):', highlightId);
                        break;
                      }
                    } catch (error) {
                      newApproachError = error;
                      console.warn('❌ NEW multiline approach failed, trying extractContents fallback:', {
                        highlightId,
                        error: error.message,
                      });
                    }
                  }

                  if (!highlighted) {
                    try {
                      if (range.collapsed || !range.startContainer || !range.endContainer) {
                        continue;
                      }

                      const contents = range.extractContents();

                      if (!contents || contents.childNodes.length === 0) {
                        continue;
                      }

                      highlightSpan.appendChild(contents);

                      if (!highlight.disable_remove) {
                        const removeBtn = document.createElement('button');
                        removeBtn.className = 'highlight-remove-btn';
                        removeBtn.innerHTML = '×';
                        removeBtn.setAttribute('aria-label', 'Remove highlight');
                        removeBtn.setAttribute('data-highlight-id', highlightId);
                        removeBtn.onclick = (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const btnHighlightId = removeBtn.getAttribute('data-highlight-id');
                          if (btnHighlightId && removeHighlight) {
                            const numericId = parseInt(btnHighlightId, 10);
                            if (!isNaN(numericId)) {
                              handleRemoveHighlightById(numericId);
                            }
                          }
                        };
                        highlightSpan.appendChild(removeBtn);
                      }

                      highlightSpan.style.cssText = `
                        background-color: rgba(34, 197, 94, 0.4) !important;
                        cursor: pointer !important;
                        position: relative !important;
                        z-index: 10 !important;
                        display: inline !important;
                        vertical-align: baseline !important;
                        line-height: inherit !important;
                        opacity: 1 !important;
                        visibility: visible !important;
                      `;
                      range.insertNode(highlightSpan);

                      void highlightSpan.offsetHeight;

                      const insertedSpan = textLayer.querySelector(`span[data-highlight-id="${highlightId}"]`);
                      if (insertedSpan) {
                        console.log('✅ Highlight span verified in DOM (extractContents fallback):', {
                          highlightId,
                          hasBackgroundColor: !!insertedSpan.style.backgroundColor,
                          backgroundColor: insertedSpan.style.backgroundColor,
                          computedDisplay: window.getComputedStyle(insertedSpan).display,
                          computedVisibility: window.getComputedStyle(insertedSpan).visibility,
                          textContent: insertedSpan.textContent.substring(0, 50),
                        });
                      } else {
                        console.warn('⚠️ Highlight span not found in DOM after insertion:', highlightId);
                      }

                      highlighted = true;
                      console.log('✅ Highlight applied successfully (extractContents fallback):', highlightId);
                      break;
                    } catch (extractError) {
                      console.warn('❌ All highlight methods failed:', {
                        highlightId,
                        surroundError: surroundError?.message,
                        newApproachError: newApproachError?.message,
                        extractError: extractError?.message,
                      });
                      console.warn('Failed to apply highlight:', extractError, {
                        highlightId,
                        highlightText: highlightText.substring(0, 50),
                        nodeText: nodeText.substring(0, 50),
                      });
                      continue;
                    }
                  }
                }
              } catch (e) {
                console.warn('Failed to create range for highlight:', e, {
                  highlightId,
                  highlightText: highlightText.substring(0, 50),
                });
                continue;
              }
            }
          }
        });
      });
    };

    let timeoutId;
    let observerTimeoutId;

    const startApplying = () => {
      applyHighlights();
      timeoutId = setTimeout(applyHighlights, 100);
    };

    const rafId = requestAnimationFrame(() => {
      startApplying();
    });

    const observer = new MutationObserver(() => {
      if (observerTimeoutId) {
        clearTimeout(observerTimeoutId);
      }

      const textLayers = document.querySelectorAll('.react-pdf__Page__textContent');
      const hasTextContent = Array.from(textLayers).some((layer) => {
        const textNodes = layer.querySelectorAll('span');
        return textNodes.length > 0 && Array.from(textNodes).some((span) => span.textContent.trim().length > 0);
      });

      if (textLayers.length > 0 && hasTextContent) {
        observerTimeoutId = setTimeout(applyHighlights, 50);
      }
    });

    const pdfContainer = containerRef.current;
    if (pdfContainer) {
      observer.observe(pdfContainer, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
      if (observerTimeoutId) clearTimeout(observerTimeoutId);
      observer.disconnect();
    };
  }, [highlights, numPages, currentPage, removeHighlight, handleRemoveHighlightById, containerRef]);
}
